import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  SimpleChange,
  ViewChild,
} from '@angular/core'
import { HlsService } from 'app/services/hls/hls.service'
import Hls from 'hls.js'

@Component({
  selector: 'video-player',
  templateUrl: './video.html',
  styleUrls: ['./video.scss'],
})
export class VideoComponent implements OnInit, OnDestroy {
  @Input() public camId: string
  @Input() public thumbnail: string
  @Input() public videoSrc: string
  @Input() public keepStreamAlive: boolean
  @ViewChild('video', { static: false }) videoElement: ElementRef

  loading: boolean = true
  client: string = ''
  showStillWatchingPrompt: boolean = false

  private hls: Hls
  private inactivityTimeout: any
  private inactivityDuration: number = 1 * 60 * 1000 // 5 minutes

  constructor(private hslService: HlsService) {}

  onVideoLoaded() {
    this.loading = false
    this.startInactivityTimer()
  }

  onVideoError() {
    // Handle error event here, possibly with a retry mechanism
    const video = this.videoElement.nativeElement
    this.retryConnection(video, this.client)
  }

  retryConnection(video, src) {
    setTimeout(() => {
      this.create(video, src)
    }, 1000)
  }

  create = (video, src) => {
    if (!video || !src) {
      console.warn('No video element or source provided.');
      return;
    }
    const deviceType = navigator.userAgent;
    const isAndroid = /android/i.test(deviceType);
    const streamUrl = `${src}index.m3u8`;

    const playVideo = () => {
      video.play().catch(err => {
        console.error('Error playing video:', err);
      });
    };
   
    // Try to play stream for IOS first then try Hls.js
    if (video.canPlayType('application/vnd.apple.mpegurl') && !isAndroid) {
      fetch(streamUrl, { method: 'HEAD' }) // Only check if stream exists
        .then(response => {
          if (!response.ok) throw new Error('Stream not available');
          video.src = streamUrl;
          video.addEventListener('loadedmetadata', playVideo, { once: true })
        })
        .catch((err) => {
          console.error('Error fetching HLS stream:', err);
        })
        return;
    }  
    
    // Fallback to Hls.js
    if (typeof Hls === 'undefined' || !Hls.isSupported()) {
      console.error('Hls.js is not supported or not loaded.');
      return;
    }

    const hls = new Hls({
      enableWorker: true, // Better performance if possible
      maxLiveSyncPlaybackRate: 1.5,
    });

    const onHlsError = (event, data) => {
      if (!data.fatal) return;
  
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          console.warn('HLS network error, trying to reload stream...');
          hls.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          console.warn('HLS media error, attempting recovery...');
          hls.recoverMediaError();
          break;
        default:
          console.error('HLS fatal error, destroying and retrying...');
          hls.destroy();
          setTimeout(() => this.create(video, src), 2000);
          break;
      }
    };

    const onManifestParsed = () => {
      playVideo();
    };
  
    hls.attachMedia(video);
    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(streamUrl);
    });
    hls.on(Hls.Events.MANIFEST_PARSED, onManifestParsed);
    hls.on(Hls.Events.ERROR, onHlsError);
  
    // Cleanup function (optional if you want to manage memory better)
    const cleanup = () => {
      hls.off(Hls.Events.ERROR, onHlsError);
      hls.off(Hls.Events.MANIFEST_PARSED, onManifestParsed);
      hls.destroy();
    };
  
    video.addEventListener('ended', cleanup, { once: true });
  }

  ngOnInit() {
    this.hslService.getStream(this.camId).then((client) => {
      const video = this.videoElement.nativeElement
      this.create(video, client)
    })
  }

  ngOnChanges(changes: {[property: string]: SimpleChange}) {
    if (changes['keepStreamAlive'] && changes['keepStreamAlive']?.previousValue != changes['keepStreamAlive']?.currentValue && this.videoElement) {
      this.continueWatching(this.videoElement.nativeElement)
    }
  }

  ngOnDestroy() {
    console.log('destroying video')
    if (this.hls) {
      this.hls.destroy() // destroy the Hls instance
    }
    const video = this.videoElement.nativeElement
    if (video) {
      console.log('clearing src')
      video.pause() // stop video playback
      video.src = '' // release the media source
      // If there are any attached Media Source Extensions, remove them
      if (video.srcObject) {
        ;(video.srcObject as MediaStream)
          .getTracks()
          .forEach((track) => track.stop())
        video.srcObject = null
      }
    }
    clearTimeout(this.inactivityTimeout)
  }

  startInactivityTimer() {
    const video = this.videoElement.nativeElement

    // Clear any existing inactivity timeout
    clearTimeout(this.inactivityTimeout)

    // Set a new inactivity timeout
    this.inactivityTimeout = setTimeout(() => {
      this.pauseAndPromptUser(video)
    }, this.inactivityDuration)
  }

  pauseAndPromptUser(video) {
    clearTimeout(this.inactivityTimeout)
    video.pause()
    this.hls.stopLoad()
    this.showStillWatchingPrompt = true
  }

  continueWatching(video) {
    this.showStillWatchingPrompt = false
    this.hls.startLoad()
    video.play()
    this.startInactivityTimer()
  }
}
